import posthog from 'posthog-js'

let initialized = false
let consented = false

/**
 * Patterns for anonymizing dynamic route segments.
 * User-generated content (project names, area names, IDs) is replaced
 * with generic placeholders so no PII leaks into PostHog pageviews.
 *
 * Example: /home/work/area/Mein%20Projekt → /home/work/area/:areaName
 */
const ANONYMIZE_PATTERNS: [RegExp, string][] = [
  [/\/area\/[^/]+/g, '/area/:areaName'],
  [/\/project\/[^/]+/g, '/project/:projectName'],
  [/\/task\/[^/]+/g, '/task/:taskId'],
]

function anonymizePath(path: string): string {
  let result = path
  for (const [pattern, replacement] of ANONYMIZE_PATTERNS) {
    result = result.replace(pattern, replacement)
  }
  return result
}

export function initAnalytics() {
  const key = import.meta.env.VITE_POSTHOG_KEY
  const host = import.meta.env.VITE_POSTHOG_HOST
  if (!key || !host) return

  posthog.init(key, {
    api_host: host,
    capture_pageview: false,   // Disabled: we send anonymized pageviews manually
    capture_pageleave: true,
    persistence: 'localStorage',
    autocapture: false,
    opt_out_capturing_by_default: true, // GDPR: opt-out by default
    before_send: (event) => {
      if (!event) return event
      // Electron URLs look like http://localhost:3000/main_window/index.html#/home/...
      // Extract the hash route and anonymize it so PostHog only sees app routes
      if (event.properties?.['$current_url']) {
        const url = String(event.properties['$current_url'])
        const hashIndex = url.indexOf('#')
        const path = hashIndex >= 0 ? url.substring(hashIndex + 1) : url
        event.properties['$current_url'] = anonymizePath(path)
      }
      return event
    },
    loaded: () => {
      // Debug mode disabled — enable with ph.debug() if needed
    }
  })
  initialized = true

  // Restore consent state
  if (getAnalyticsConsent()) {
    posthog.opt_in_capturing()
    consented = true
  }
}

/**
 * Check if the user has given analytics consent.
 */
export function getAnalyticsConsent(): boolean {
  return localStorage.getItem('analytics-consent') === 'true'
}

/**
 * Set analytics consent. Enables or disables PostHog capturing.
 */
export function setAnalyticsConsent(value: boolean): void {
  consented = value
  localStorage.setItem('analytics-consent', value ? 'true' : 'false')

  if (!initialized) return

  if (value) {
    posthog.opt_in_capturing()
  } else {
    posthog.opt_out_capturing()
  }
}

export function identifyUser(userId: string, properties?: Record<string, any>) {
  if (!initialized || !consented) return
  posthog.identify(userId, properties)
}

export function trackEvent(event: string, properties?: Record<string, any>) {
  if (!initialized || !consented) return
  posthog.capture(event, properties)
}

/**
 * Send a pageview with anonymized path.
 * Dynamic segments (area names, project names) are replaced with placeholders.
 */
export function trackPageview(path: string) {
  if (!initialized || !consented) return
  posthog.capture('$pageview', { $current_url: anonymizePath(path) })
}

export function resetAnalytics() {
  if (!initialized) return
  posthog.reset()
}
